<template>
  <div class="LmInput relative mb-8">
    <label
      class="block mb-1 font-mono text-xxs uppercase tracking-wider text-gray-500"
      :for="randomId"
      >{{ label }}</label
    >
    <input
      :id="randomId"
      ref="LmInput"
      v-model="data"
      v-focus="focus"
      class="w-full text-s leading-loose border-b appearance-none"
      :class="inputClasses"
      autocomplete="on"
      :type="type"
      :maxlength="length"
      :required="!optional"
      :disabled="disabled"
      @focus="onFocus"
      @input="onInput($event.target.value)"
      @change="onChange($event)"
      @blur="onBlur"
      @keydown.enter="onKeydownEnter"
    />
    <div class="absolute w-full flex items-center mt-1 text-xs ">
      <span v-if="error" class="text-error">{{ errorMessage }}</span>
      <span v-else-if="help" class="text-gray-500">{{ help }}</span>
      <span v-if="length && !disabled" class="ml-auto text-gray-500">
        {{ dataLength }}/{{ length }}
      </span>
    </div>
  </div>
</template>

<script>
import validate from 'validate.js'
import * as RULES from './validation-rules'

export default {
  name: 'LmInput',
  props: {
    type: {
      type: String,
      default: 'text'
    },
    label: {
      type: String,
      required: true
    },
    focus: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: Number,
      default: 50
    },
    optional: {
      type: Boolean,
      default: false
    },
    help: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    customRule: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      data: '',
      error: false,
      errorMessage: '',
      init: false,
      active: false
    }
  },
  computed: {
    inputClasses() {
      return [
        {'border-error': this.error},
        this.disabled
          ? 'text-gray-500 bg-white cursor-not-allowed'
          : 'text-gray-900'
      ]
    },
    randomId() {
      return Math.random()
        .toString(36)
        .substring(2, 15)
    },
    length() {
      // maxlength === 0 = null
      return this.maxlength || null
    },
    dataLength() {
      return this.data ? this.data.length : 0
    }
  },
  methods: {
    onFocus() {
      if (!this.active) this.active = true
      this.clearErrors()
      this.$emit('focus')
    },
    onInput(value) {
      this.$store.dispatch('setDirty')
      if (!this.init) this.init = true
      if (this.error) this.clearErrors()
      this.$emit('input', value)
    },
    onChange(e) {
      this.$emit('change', e)
    },
    onBlur(e) {
      this.active = false
      this.$emit('blur', e)
    },
    onKeydownEnter(e) {
      this.$emit('submit', e)
    },
    validateData() {
      if (this.optional) {
        return true
      }
      this.error = false
      let rule = this.customRule ? this.customRule : this.type
      let v = validate({data: this.data}, RULES[rule], {
        fullMessages: false
      })
      if (v === undefined) {
        return true
      }
      this.error = true
      this.errorMessage = v.data[0]
      return false
    },
    clearErrors() {
      this.error = false
      this.errorMessage = ''
    },
    reset() {
      this.data = ''
      this.init = false
      this.active = false
      this.clearErrors()
    },
    // Public
    setError(message) {
      this.error = true
      this.errorMessage = message
    },
    setData(data) {
      this.data = data
      this.init = false
    },
    setFocus() {
      this.$refs.LmInput.focus()
    }
  }
}
</script>

<style>
.LmInput input:focus {
  outline: none;
  border: none; /* override global */
  border-bottom: 1px solid theme('colors.gray.900');
}
</style>
